import { CoreEvent } from "@nrk/player-core/types.js";
import { ReactiveState } from "@nrk/reactive-element";
import { getLogger } from "../utils/logging.js";
import platform from "../utils/platform.js";
import { NO_SUBTITLES } from "./player-types.js";
const _logger = getLogger("SubtitleState");
class SubtitleState extends ReactiveState {
  /** Flag for subtitle visibility */
  isShowing = false;
  /** The currently active track label */
  activeLabel;
  /** The currently visible subtitle cues */
  activeCues = [];
  /** The subtitle tracks available for the stream */
  subtitles = [];
  /** If the subtitles are rendered natively or not */
  nativeDisplayEnabled;
  _session;
  constructor(session, manifest, preferredSubtitle) {
    super();
    this._session = session;
    this.nativeDisplayEnabled = session.isSubtitlesDisplayed();
    this._init(manifest);
    session.on(CoreEvent.SubtitlesCueUpdate, this._onSubtitlesCueUpdate, this);
    session.on(CoreEvent.SubtitlesChange, this._onSubtitlesChange, this);
    session.on(CoreEvent.SubtitlesDisplayChange, this._onSubtitlesDisplayChange, this);
    if (session.getAvailableSubtitles().length === 0) {
      session.once(CoreEvent.SubtitlesChange, () => this._applyPreferredSubtitle(preferredSubtitle));
    } else {
      this._applyPreferredSubtitle(preferredSubtitle);
    }
  }
  /**
   * Toggle subtitle visibility
   * @todo - add support for choosing track
   */
  toggleVisibility() {
    if (!this._session) {
      return;
    }
    if (this.isShowing) {
      this.isShowing = false;
      this._session.selectNoSubtitles();
    } else {
      const availableSubtitles = this._session.getAvailableSubtitles();
      let subtitles = availableSubtitles.find((sub) => sub.flags.isDefault);
      if (subtitles === void 0) {
        subtitles = availableSubtitles.find((sub) => sub.flags.isCaptions);
      }
      if (subtitles === void 0) {
        subtitles === availableSubtitles.find((sub) => sub.flags.isAutoSelectable);
      }
      if (subtitles === void 0) {
        this._session.selectNoSubtitles({ byUser: false });
        this.isShowing = false;
      } else {
        this._session.selectSubtitles(subtitles, { byUser: false });
        this.isShowing = true;
      }
    }
  }
  destroy() {
    super.destroy();
    this._session = void 0;
  }
  async _init(manifest) {
    let subtitles = this._session.getAvailableSubtitles();
    if (manifest) {
      if (subtitles?.length === 0 && this._session.canAddSubtitles() && platform.isTizenDevice()) {
        await addExternalSubtitles(this._session, manifest);
      }
      if (!this._session) {
        return;
      }
      subtitles = this._session.getAvailableSubtitles();
    }
    this.activeLabel = this._session.getActiveSubtitles()?.label;
    this.isShowing = this.activeLabel !== void 0;
    this.subtitles = subtitles;
  }
  _applyPreferredSubtitle(preferredSubtitle) {
    _logger.log(
      "Applying user subtitle preference:",
      preferredSubtitle === void 0 ? "no preference/default" : preferredSubtitle
    );
    if (preferredSubtitle === NO_SUBTITLES) {
      this._session.selectNoSubtitles({ byUser: false });
    } else if (preferredSubtitle !== void 0) {
      this._session.selectSubtitles(preferredSubtitle);
    } else {
      const defaultSubtitles = this._session.getAvailableSubtitles().find((sub) => sub.flags.isDefault);
      if (defaultSubtitles !== void 0) {
        this._session.selectSubtitles(defaultSubtitles, { byUser: false });
      } else {
        this._session.selectNoSubtitles({ byUser: false });
      }
    }
  }
  _onSubtitlesCueUpdate(event) {
    this.activeCues = event.activeCues;
  }
  _onSubtitlesChange(event) {
    if (!this.subtitles || this.subtitles.length === 0) {
      this.subtitles = this._session.getAvailableSubtitles();
    }
    this.activeLabel = event.active?.label ?? NO_SUBTITLES;
    this.isShowing = this.activeLabel !== NO_SUBTITLES;
  }
  _onSubtitlesDisplayChange(event) {
    this.nativeDisplayEnabled = event.displayed;
  }
}
async function addExternalSubtitles(session, manifest) {
  const subtitles = manifest.playable.subtitles.map((sub) => {
    const isCaptions = sub.type === "mix";
    const isDefault = sub.defaultOn;
    return {
      src: sub.webVtt,
      label: sub.label,
      language: sub.language,
      flags: {
        isCaptions,
        isDefault
      }
    };
  });
  _logger.debug("Adding external subtitles:", subtitles);
  try {
    await session.addSubtitles(subtitles);
  } catch (error) {
    _logger.error(error);
  }
}
export {
  SubtitleState
};
